import React, { useState } from 'react'
import { ButtonLink, MultiLinkBlock } from '@which/seatbelt'
import { ExternalLinkIcon } from '@which/seatbelt/src/components/Icons/Navigational/ExternalLink'
import { TypographyV2 } from '@which/seatbelt/src/components/Seatbelt2/Foundations/Typography'
import { dynamicGa4DataLayerPush } from '@which/shared'

import classnames from 'classnames'

import { extractContentIdFromUrl } from '../../utils/get-tracking-info'
import {
  retirementStatusOptions,
  retirementStatusOptionsMap,
} from './RetirementStatusSelector.constants'
import styles from './RetirementStatusSelector.module.scss'
import type { RetirementStatus } from './RetirementStatusSelector.types'

export const RetirementStatusSelector = ({
  className,
  position,
}: RetirementStatusSelectorProps) => {
  const [selectedStatus, setSelectedStatus] = useState<RetirementStatus | null>()
  const selectedStatusValue = selectedStatus ? retirementStatusOptionsMap[selectedStatus] : null

  const handleStatusSelect = (status: RetirementStatus, index: number) => {
    if (selectedStatus === status) {
      return setSelectedStatus(null)
    }

    dynamicGa4DataLayerPush({
      event: 'personalisation_tool_question',
      item_group: 'personalisation tools',
      tool_name: 'Retirement planning tool',
      item_question: 'How close are you to retirement?',
      item_answer: retirementStatusOptionsMap[status].label,
      item_spec: position,
      item_index: index,
      item_parent_text: undefined,
    })

    setSelectedStatus(status)
  }

  const handleOnwardJourneyLinkClick = ({
    text,
    url,
    index,
  }: {
    text: string
    url: string
    index: number
  }) => {
    dynamicGa4DataLayerPush({
      event: 'personalisation_tool_onward_journey',
      item_group: 'personalisation tools',
      tool_name: 'Retirement planning tool',
      item_text: text,
      item_parent_text: undefined,
      item_spec: position,
      item_url: url,
      item_index: index,
      item_personalisation: 'true',
      item_content_id: extractContentIdFromUrl(url) ?? undefined,
      item_product_id: undefined,
      item_model_id: undefined,
    })
  }

  return (
    <div className={className}>
      <TypographyV2
        id="retirement-status-selector-heading"
        textStyle="sb-text-heading-small"
        className={styles.title}
        tag="h2"
      >
        How close are you to retirement?
      </TypographyV2>

      <TypographyV2 textStyle="sb-text-body-default-regular" className={styles.subtitle} tag="p">
        Get recommendations based on your retirement planning stage
      </TypographyV2>

      <div
        className={styles.cardsContainer}
        role="radiogroup"
        aria-labelledby="retirement-status-selector-heading"
      >
        {retirementStatusOptions.map((option, index) => {
          const isSelected = selectedStatus === option.value

          return (
            <button
              key={option.value}
              className={classnames(styles.card, isSelected && styles.selected)}
              role="radio"
              aria-checked={isSelected}
              onClick={() => handleStatusSelect(option.value, index + 1)}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {selectedStatusValue ? (
        // Header is visually hidden via CSS,
        // but the title is still used for the aria-label inside the component
        <MultiLinkBlock
          className={styles.linksContainer}
          componentType={'retirement-status-selector-' + position + selectedStatusValue.value}
          headerTag="h2"
          title="Recommended"
          links={selectedStatusValue.links.map((link, index) => ({
            ...link,
            onClick: () =>
              handleOnwardJourneyLinkClick({
                text: link.text,
                url: link.href,
                index: index + 1,
              }),
          }))}
        ></MultiLinkBlock>
      ) : null}

      {selectedStatusValue?.cta ? (
        <div className={styles.ctaContainer} data-testid="retirement-status-selector-cta">
          <TypographyV2
            id="retirement-status-selector-cta-label"
            textStyle="sb-text-body-small-regular"
            className={styles.ctaTitle}
            tag="p"
          >
            {selectedStatusValue.cta.label}
          </TypographyV2>
          <ButtonLink
            href={selectedStatusValue.cta.button.href}
            className={styles.ctaButton}
            target="_blank"
            aria-describedby="retirement-status-selector-cta-label"
            onClick={() =>
              handleOnwardJourneyLinkClick({
                text: selectedStatusValue.cta!.button.label,
                url: selectedStatusValue.cta!.button.href,
                index: selectedStatusValue.links.length + 1,
              })
            }
          >
            <ExternalLinkIcon></ExternalLinkIcon>
            {selectedStatusValue.cta.button.label}
          </ButtonLink>
          <TypographyV2
            textStyle="sb-text-body-x-small-regular"
            className={styles.ctaDisclaimer}
            tag="p"
          >
            {selectedStatusValue.cta.disclaimer}
          </TypographyV2>
        </div>
      ) : null}
    </div>
  )
}

export interface RetirementStatusSelectorProps {
  className?: string
  position: 'bottom' | 'sidebar'
}
